import { Outlet, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";

// components
import { LoaderMUI } from "../../assets/global/loader/loader-mui";
import { AlertScreen } from "./alert-screen";
import { NoAccessScreen } from "./no-access-screen";
import { NavHeaderShort } from "./nav-header-short";

// services
import {
  apiGetUser,
  apiGetUserProjectPermission,
} from "../../services/api/auth-api";
import { useUserContext } from "../../services/context/user-context";
import { luckyorangeIdentifyUser } from "../../services/lucky-orange/luckyorange-events";
import { isAxiosApiError } from "../../services/api/api";
import { apiGetSurvey } from "../../services/api/survey.api";

// constants
import { APPS, ROLES } from "../../constants/global.constants";
import { AppProvider } from "../../services/context/app-context";
import { NavHeader } from "./nav-header";

export const MainLayout = () => {
  const [searchParams] = useSearchParams();
  const userToken = searchParams.get("token");
  const { companyId, projectId } = useParams();

  const {
    userData,
    setUserData,
    userProjectPermission,
    setUserProjectPermission,
    setViewOnly,
    setSurveyTallyId
  } = useUserContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

    const fetchSurvey = async () => {
    if (!userToken || !companyId || !projectId) return;
    /* setError(null); */

    try {
      const responseSurvey =
        await apiGetSurvey(projectId);

      if (responseSurvey) {
        setSurveyTallyId(responseSurvey.tallyId);
      } else {
        setSurveyTallyId(null);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      /* if (err instanceof ApiErrorClass) {
        setError(err.message);
      } else {
        setError("Error fetching data");
      } */
      setSurveyTallyId(null);
    } finally {
      /* setLoading(false); */
    }
  };

  const fetchUser = async () => {
    if (!userToken || !companyId || !projectId) return;
    setError(null);

    try {
      const responseUser = await apiGetUser({
        idToken: userToken,
        companyId: companyId,
      });

      if (responseUser) {
        const responseProjectPermission = await apiGetUserProjectPermission({
          user: responseUser.email,
          projectId,
        });

        setUserProjectPermission(responseProjectPermission ?? null);

        setUserData(responseUser);
        setViewOnly(
          responseUser.apps?.MASTER === ROLES.OWNER
            ? false
            : responseProjectPermission
              ? responseProjectPermission.role === ROLES.VIEWER
              : responseUser.apps?.MASTER === ROLES.VIEWER,
        );
        localStorage.setItem("token", userToken);
        localStorage.setItem("companyId", companyId);

        fetchSurvey();
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      let message;
      if (isAxiosApiError(err)) {
        message =
          err.response?.status === 401
            ? "Token expired — please refresh the tab."
            : (err.response?.data?.message ?? err.message);
      } else {
        message = "Error fetching data";
      }
      setError(message);
      setUserData(null);
      localStorage.removeItem("token");
      localStorage.removeItem("companyId");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [userToken, companyId]);

  useEffect(() => {
    if (
      userData &&
      !userData.isAdmin /* && process.env.NODE_ENV === 'production' */
    ) {
      const script = document.createElement("script");
      script.src = "https://tools.luckyorange.com/core/lo.js?site-id=31468eb6";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);

      /* window.__wtw_lucky_do_not_record = true; */
      if (userData.email) {
        luckyorangeIdentifyUser(userData.email);
      }

      // 3) Add a global error handler
      /* function handleGlobalError(event) {
      // event.message can contain the error message
      // event.error might contain the Error object
      if (event.message && event.message.includes("Blocked a frame with origin")) {
        console.log(event.message, event)
        // Prevent it from showing a big error or halting the app
        event.preventDefault();
      }
    }

    window.addEventListener("error", handleGlobalError);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("error", handleGlobalError);
    }; */
    }
  }, [userData]);

  if (loading) return <LoaderMUI withBackdrop={true} />;
  if (error || !userData) return <AlertScreen />;
  if (
    !userData.isAdmin &&
    ((userData && !userData.apps?.MASTER) ||
      (userData.apps?.MASTER !== ROLES.OWNER &&
        !userProjectPermission?.hasPermission))
  )
    return <NoAccessScreen app={APPS.MASTER} />;

  return (
    <AppProvider>
      <NavHeaderShort />
      <Outlet />
    </AppProvider>
  );
};
